import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/zoom.css";

import { Menu, MenuItem } from "@szhsin/react-menu";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineNotifications } from "react-icons/md";
import { useLocation } from "react-router-dom";

import { useNotification } from "../context/NotificationContext";
import useAuth from "../hooks/useAuth";
import { useHandleNotificationClick } from "../hooks/useHandleNotificationClick";
import authServices from "../services/authServices";
import { getLanguage } from "../utils/getLanguage";
import { useNavigateWithLang } from "../utils/navigation";
import Button from "./Button";
import CodeNotificationLevelProjectModal from "./modals/common/CodeNotificationLevelProjectModal";
import CodeNotificationSubmissionsModal from "./modals/common/CodeNotificationSubmissionsModal";

const NotificationDropdown = () => {
  const { unreadCount, setNotifications } = useNotification();
  const [localNotifications, setLocalNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigateWithLang();
  const location = useLocation();
  const lang = getLanguage();
  const user = useAuth();
  const role = user?.role?.toLowerCase() || "student";

  const { handleClick, studentCodeSolutionsModalRef, studentLevelProjectModalRef } =
    useHandleNotificationClick();

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await authServices.getNotifications({ page: 1, per_page: 5 });
      setLocalNotifications(response.data);
      setNotifications(response.data); // optionally update global
    } catch (error) {
      console.error("Failed to fetch Notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Menu
        menuButton={() => (
          <button
            onClick={fetchNotifications}
            disabled={location.pathname.includes("notifications-center")}
            className="relative"
          >
            <Button
              color={role == "student" ? "" : "white"}
              className={`${
                location.pathname.includes("notifications-center")
                  ? role == "student"
                    ? "bg-blue-600"
                    : "!bg-gray-100"
                  : ""
              }`}
              icon={
                <MdOutlineNotifications
                  className={role === "student" ? "text-white" : "text-black"}
                  size={20}
                />
              }
            />

            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 rounded-full bg-red-500 px-1 text-xs text-white">
                {unreadCount}
              </span>
            )}
          </button>
        )}
        transition
        align="end"
        menuClassName="max-h-96 w-80 overflow-y-auto rounded-lg bg-white shadow-lg dark:bg-dark-surface"
      >
        <div className="border-b px-4 py-2 text-sm font-semibold text-gray-700 dark:text-white">
          {t("notifications")}
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-4">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
          </div>
        ) : localNotifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {t("no_notifications")}
          </div>
        ) : (
          localNotifications.map((n) => {
            const message =
              typeof n.message === "string"
                ? n.message
                : lang === "ar"
                  ? n.message?.ar
                  : n.message?.en || "New notification";

            return (
              <MenuItem
                key={n.id}
                onClick={() => handleClick(n)}
                className={`flex gap-3 border-b p-3 transition-colors ltr:text-left rtl:text-right ${
                  !n.read_at ? "bg-blue-50 dark:bg-blue-900" : "bg-white dark:bg-dark-surface"
                } hover:bg-gray-100 dark:hover:bg-gray-700`}
              >
                <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 text-center text-xl leading-10 text-white dark:bg-gray-600">
                  🔔
                </div>
                <div className="flex-1 text-xs text-gray-600 dark:text-gray-300">{message}</div>
                {!n.read_at && <span className="mt-1 h-2 w-2 self-start rounded-full bg-blue-500" />}
              </MenuItem>
            );
          })
        )}

        <MenuItem
          onClick={() => navigate(`/${role}/notifications-center`)}
          className="cursor-pointer justify-center border-t p-3 text-center text-sm font-semibold text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900"
        >
          See All
        </MenuItem>
      </Menu>
      <CodeNotificationSubmissionsModal ref={studentCodeSolutionsModalRef} />
      <CodeNotificationLevelProjectModal ref={studentLevelProjectModalRef} />
    </>
  );
};

export default NotificationDropdown;
